/**
 * Mobile Account Submit Button Component
 * Sticky bottom bar with Cancel (edit mode) and Submit buttons.
 * Bar stays at bottom and sits under the keyboard layer (lower z-index than calculator).
 */

import { Paper, Button, Stack, CircularProgress } from "@mui/material";

export interface MobileAccountSubmitButtonProps {
  isEditMode: boolean;
  isSubmitting: boolean;
  hasChanges: boolean;
  isFormValid: boolean;
  onCancel: () => void;
  /** Unused: bar always at bottom, under keyboard when it opens */
  calculatorOpen?: boolean;
}

export default function MobileAccountSubmitButton({
  isEditMode,
  isSubmitting,
  hasChanges,
  isFormValid,
  onCancel,
}: MobileAccountSubmitButtonProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderTop: 1,
        borderColor: "divider",
        px: 2,
        py: 1.5,
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
        zIndex: 1200,
        bgcolor: "background.paper",
      }}
    >
      <Stack direction="row" spacing={2}>
        {isEditMode && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{ height: "48px", minHeight: "48px" }}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          form="account-form"
          fullWidth={!isEditMode}
          variant="contained"
          color="primary"
          size="large"
          disabled={isSubmitting || (isEditMode ? !hasChanges : !isFormValid)}
          sx={{
            height: "48px",
            minHeight: "48px",
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : isEditMode ? "Update" : "Create"}
        </Button>
      </Stack>
    </Paper>
  );
}
